import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Combobox reutilizável — Popover + Command com busca.
 * Suporta `allowFreeText` (permite selecionar valor digitado que não está em options).
 */
export function Combobox({ value, onChange, options = [], placeholder = "Selecione", searchPlaceholder = "Buscar...", disabled = false, allowFreeText = false, ...rest }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const label = useMemo(() => options.find((o) => o.value === value)?.label || (allowFreeText && value ? value : ""), [options, value, allowFreeText]);

  const filtered = useMemo(() => {
    if (!q) return options;
    const qn = q.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(qn));
  }, [options, q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled}
          className={cn("w-full justify-between font-normal", !label && "text-muted-foreground")}
          {...rest}>
          {label || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-[90vw]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={q} onValueChange={setQ} />
          <CommandList className="max-h-64">
            <CommandEmpty>
              {allowFreeText && q ? (
                <button type="button" className="w-full text-left px-2 py-1.5 hover:bg-accent rounded text-sm"
                  onClick={() => { onChange(q); setOpen(false); setQ(""); }}>
                  Usar "<b>{q}</b>" manualmente
                </button>
              ) : "Nenhum resultado"}
            </CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 200).map((o) => (
                <CommandItem key={o.value} value={o.label}
                  onSelect={() => { onChange(o.value); setOpen(false); setQ(""); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
